/**
 * Build the Verb Conjugator's mood-aware tables from a flat
 * [{text, tags}] conjugation list (the /api/conjugator/conjugate/ shape,
 * same closed tag vocabulary as VerbConjugation rows).
 *
 * Deliberately separate from ParadigmSheet's groupConjugations: that one
 * serves curated, sparsely-tagged dictionary data and folds all moods into
 * one "present" bucket; the conjugator's AI output always carries a mood
 * tag per non-past row and wants one table per mood.
 */

// Canonical paradigm order: 3rd → 2nd → 1st person, masculine before
// feminine, singular → dual → plural. Keyed by the exact person/gender/
// number tag set (1st person and 2nd dual carry no gender tag).
export const PERSON_SLOTS = [
  { tags: ["third-person", "masculine", "singular"], pronoun: "هُوَ", label: "3rd masc. sg." },
  { tags: ["third-person", "feminine", "singular"], pronoun: "هِيَ", label: "3rd fem. sg." },
  { tags: ["third-person", "masculine", "dual"], pronoun: "هُمَا", label: "3rd masc. dual" },
  { tags: ["third-person", "feminine", "dual"], pronoun: "هُمَا", label: "3rd fem. dual" },
  { tags: ["third-person", "masculine", "plural"], pronoun: "هُمْ", label: "3rd masc. pl." },
  { tags: ["third-person", "feminine", "plural"], pronoun: "هُنَّ", label: "3rd fem. pl." },
  { tags: ["second-person", "masculine", "singular"], pronoun: "أَنْتَ", label: "2nd masc. sg." },
  { tags: ["second-person", "feminine", "singular"], pronoun: "أَنْتِ", label: "2nd fem. sg." },
  { tags: ["second-person", "dual"], pronoun: "أَنْتُمَا", label: "2nd dual" },
  { tags: ["second-person", "masculine", "plural"], pronoun: "أَنْتُمْ", label: "2nd masc. pl." },
  { tags: ["second-person", "feminine", "plural"], pronoun: "أَنْتُنَّ", label: "2nd fem. pl." },
  { tags: ["first-person", "singular"], pronoun: "أَنَا", label: "1st sg." },
  { tags: ["first-person", "plural"], pronoun: "نَحْنُ", label: "1st pl." },
];

const PGN_TAGS = new Set([
  "first-person", "second-person", "third-person",
  "masculine", "feminine", "singular", "dual", "plural",
]);

const SLOT_KEYS = PERSON_SLOTS.map((s) => [...s.tags].sort().join("|"));

/** Index into PERSON_SLOTS for a row's tags, or -1 when they don't name a slot. */
export function slotIndex(tags) {
  const key = tags.filter((t) => PGN_TAGS.has(t)).sort().join("|");
  return SLOT_KEYS.indexOf(key);
}

const TABLES = [
  {
    key: "active_past", label: "Past", labelAr: "الماضي",
    match: (s) => s.has("past") && !s.has("passive"),
  },
  {
    key: "active_present_indicative", label: "Present (indicative)", labelAr: "المضارع المرفوع",
    match: (s) => s.has("non-past") && !s.has("passive") && !s.has("subjunctive") && !s.has("jussive"),
  },
  {
    key: "active_present_subjunctive", label: "Present (subjunctive)", labelAr: "المضارع المنصوب",
    match: (s) => s.has("non-past") && !s.has("passive") && s.has("subjunctive"),
  },
  {
    key: "active_present_jussive", label: "Present (jussive)", labelAr: "المضارع المجزوم",
    match: (s) => s.has("non-past") && !s.has("passive") && s.has("jussive"),
  },
  {
    key: "imperative", label: "Imperative", labelAr: "الأمر",
    match: (s) => s.has("imperative"),
  },
  {
    key: "passive_past", label: "Past (passive)", labelAr: "الماضي المبني للمجهول",
    match: (s) => s.has("past") && s.has("passive"),
  },
  {
    key: "passive_present_indicative", label: "Present (passive)", labelAr: "المضارع المبني للمجهول",
    match: (s) => s.has("non-past") && s.has("passive") && !s.has("subjunctive") && !s.has("jussive"),
  },
  {
    key: "passive_present_subjunctive", label: "Present (passive, subjunctive)", labelAr: "المضارع المجهول المنصوب",
    match: (s) => s.has("non-past") && s.has("passive") && s.has("subjunctive"),
  },
  {
    key: "passive_present_jussive", label: "Present (passive, jussive)", labelAr: "المضارع المجهول المجزوم",
    match: (s) => s.has("non-past") && s.has("passive") && s.has("jussive"),
  },
];

/**
 * -> { tables: [{key, label, labelAr, rows: [{text, tags, pronoun, slotLabel}]}],
 *      derivatives: [{text, tags}] }
 * Tables appear in canonical order, only when they have rows; rows are
 * sorted by the 13-slot order (unslottable rows sink to the end in input
 * order). Participle/verbal-noun rows come back separately — the header
 * card shows them, not a table.
 */
export function buildParadigmTables(conjugations) {
  const tables = TABLES.map((t) => ({ key: t.key, label: t.label, labelAr: t.labelAr, rows: [] }));
  const derivatives = [];
  (conjugations || []).forEach((row, i) => {
    if (!row || !row.text) return;
    const tags = row.tags || [];
    const s = new Set(tags);
    if (s.has("participle") || s.has("verbal-noun")) {
      derivatives.push(row);
      return;
    }
    const tableIdx = TABLES.findIndex((t) => t.match(s));
    if (tableIdx === -1) return;
    const slot = slotIndex(tags);
    tables[tableIdx].rows.push({
      ...row,
      pronoun: slot >= 0 ? PERSON_SLOTS[slot].pronoun : "",
      slotLabel: slot >= 0 ? PERSON_SLOTS[slot].label : "",
      _sort: slot >= 0 ? slot : PERSON_SLOTS.length + i,
    });
  });
  for (const t of tables) {
    t.rows.sort((a, b) => a._sort - b._sort);
    t.rows.forEach((r) => delete r._sort);
  }
  return { tables: tables.filter((t) => t.rows.length), derivatives };
}
